import { Chip, Stack, Typography } from '@mui/material';
import { defineMessage, FormattedMessage } from 'react-intl';

const confidenceLabelMessage = defineMessage({
  id: 'logger.diarization.confidence',
  defaultMessage: 'Confianza',
});

const highMessage = defineMessage({
  id: 'audit.transcriptionScreen.high',
  defaultMessage: 'Alta',
});

const mediumMessage = defineMessage({
  id: 'audit.transcriptionScreen.medium',
  defaultMessage: 'Media',
});

const lowMessage = defineMessage({
  id: 'audit.transcriptionScreen.low',
  defaultMessage: 'Baja',
});

type ConfidenceTone = 'success' | 'warning' | 'error' | 'default';

/**
 * Maps engine distance (lower = better match) to a confidence tone and label.
 */
function getConfidenceTone(distancePct: number): {
  tone: ConfidenceTone;
  message: typeof highMessage;
} {
  if (distancePct <= 30) {
    return { tone: 'success', message: highMessage };
  }
  if (distancePct <= 35) {
    return { tone: 'warning', message: mediumMessage };
  }
  if (distancePct > 36) {
    return { tone: 'error', message: lowMessage };
  }
  return { tone: 'default', message: mediumMessage };
}

type DiarizationConfidenceBadgeProps = {
  /** Engine distance score; lower values mean a stronger voice match. */
  distance: number;
};

/**
 * Compact confidence row for the diarization popup: label + colored chip with score.
 */
export function DiarizationConfidenceBadge({ distance }: DiarizationConfidenceBadgeProps) {
  const distancePct = Math.round(distance * 100);
  const score = Math.round(distance * 100) / 100;
  const { tone, message } = getConfidenceTone(distancePct);

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25 }}>
      <Typography variant="caption" color="text.secondary">
        <FormattedMessage {...confidenceLabelMessage} />
      </Typography>
      <Chip
        size="small"
        color={tone === 'default' ? undefined : tone}
        variant="outlined"
        label={
          <>
            <FormattedMessage {...message} />
            {' · '}
            {score.toFixed(2)}
          </>
        }
        sx={{ height: 24, '& .MuiChip-label': { px: 1 } }}
      />
    </Stack>
  );
}
